// utils/createTinyAvatarFile.js

export default async function createTinyAvatarFile({
  svg,
  fileName = "avatarTiny.png",
  outputSize = 256,
  flip = true,
  paddingRatio = 0.08,
}) {
  if (!svg) {
    throw new Error("Avatar SVG not found");
  }

  const bbox = svg.getBBox();

  if (!bbox.width || !bbox.height) {
    throw new Error("Avatar SVG has no visible bounds");
  }

  const padding =
    Math.max(bbox.width, bbox.height) * paddingRatio;

  const contentWidth =
    bbox.width + padding * 2;

  const contentHeight =
    bbox.height + padding * 2;

  const side =
    Math.max(contentWidth, contentHeight);

  const centerX =
    bbox.x + bbox.width / 2;

  const centerY =
    bbox.y + bbox.height / 2;

  const clone = svg.cloneNode(true);

  clone.setAttribute(
    "xmlns",
    "http://www.w3.org/2000/svg",
  );

  // Standalone SVGs don't inherit the app's CSS,
  // so explicitly preserve overflow for hair/accessories.
  clone.setAttribute("overflow", "visible");
  clone.style.overflow = "visible";

  clone
    .querySelectorAll("svg")
    .forEach((nestedSvg) => {
      nestedSvg.setAttribute(
        "overflow",
        "visible",
      );
      nestedSvg.style.overflow =
        "visible";
    });

  clone.setAttribute(
    "viewBox",
    `${
      centerX - side / 2
    } ${
      centerY - side / 2
    } ${side} ${side}`,
  );

  clone.setAttribute(
    "width",
    outputSize,
  );

  clone.setAttribute(
    "height",
    outputSize,
  );

  const svgString =
    new XMLSerializer().serializeToString(
      clone,
    );

  const svgBlob = new Blob(
    [svgString],
    {
      type: "image/svg+xml;charset=utf-8",
    },
  );

  const objectUrl =
    URL.createObjectURL(svgBlob);

  try {
    const image = new Image();

    await new Promise(
      (resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = objectUrl;
      },
    );

    const canvas =
      document.createElement("canvas");

    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx =
      canvas.getContext("2d");

    if (!ctx) {
      throw new Error(
        "Could not create avatar canvas",
      );
    }

    // Transparent by default.
    ctx.clearRect(
      0,
      0,
      outputSize,
      outputSize,
    );

    if (flip) {
      ctx.save();

      ctx.translate(
        outputSize,
        0,
      );

      ctx.scale(-1, 1);
    }

    ctx.drawImage(
      image,
      0,
      0,
      outputSize,
      outputSize,
    );

    if (flip) {
      ctx.restore();
    }

    const blob =
      await new Promise((resolve) => {
        canvas.toBlob(
          resolve,
          "image/png",
          1,
        );
      });

    if (!blob) {
      throw new Error(
        "Could not create avatar image",
      );
    }

    return new File(
      [blob],
      fileName,
      {
        type: "image/png",
      },
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}